import './AmcNewToBrand.scss';
import { endOfMonth, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { useSetError } from '@src/redux/modals';
import { Channel4, Channel6, Channel7, Channel8, GreenHeat100 } from '@src/utils/colors';
import { download } from '@src/utils/download-utils';
import { AmcLineChart } from '@Components/AmcLineChart';
import { AmcStackedBarChart } from '@Components/AmcStackedBarChart';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { ChartContainer } from '@Components/ChartContainer';
import { Design } from '@Components/designConstants';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner/Spinner';
import { getNtbTableHeaders, NewToBrandMaxValues } from './amc-new-to-brand-config';
import { AmcNewToBrandDataCardRow } from './AmcNewToBrandDataCardRow';
import { useAmcNewToBrandData } from './utils/useAmcNewToBrandData';

const DATE_FORMAT = 'yyyy-MM-dd';

// Previous month's report isn't prepared until the 15th of the current month.
const monthsBack = new Date().getDate() < 15 ? 2 : 1;

const DEFAULT_DATE_RANGE: DateRange = {
  start: format(startOfMonth(subMonths(new Date(), monthsBack + 2)), DATE_FORMAT),
  end: format(startOfMonth(subMonths(new Date(), monthsBack)), DATE_FORMAT)
};

const NoNewToBrandData = () => <div className='amc-new-to-brand-no-data'>No data to show</div>;

interface AmcNewToBrandProps {
  amcInstanceId?: string;
}

export const AmcNewToBrand = ({ amcInstanceId }: AmcNewToBrandProps) => {
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [behaviorByGroupCsv, setBehaviorByGroupCsv] = useState('');
  const setError = useSetError();

  const {
    data: ntbData,
    isLoading,
    isFetching,
    error
  } = useAmcNewToBrandData(
    amcInstanceId,
    dateRange.start,
    format(endOfMonth(parseISO(dateRange.end)), DATE_FORMAT)
  );

  useEffect(() => {
    if (error) {
      setError({
        title: 'Error fetching New to Brand data',
        message: 'There was an error fetching the New to Brand data. Please try again later.'
      });
    }
  }, [error, setError]);

  const maxValues = useMemo<NewToBrandMaxValues>(
    () => ({
      maxNtbRate: Math.max(0, ...(ntbData?.behaviorByGroup.map(row => row.ntbRate) ?? [])),
      maxNtbRepeatRate: Math.max(
        0,
        ...(ntbData?.behaviorByGroup.map(row => row.ntbRepeatRate) ?? [])
      )
    }),
    [ntbData]
  );

  // bpm-frontend computed the revenue card's NTB purchase rate client-side from the table rows
  // rather than having the API return it directly.
  const revenueCardData = useMemo(() => {
    if (!ntbData?.overview.revenue) return undefined;
    const totalNtbPurchases = ntbData.behaviorByGroup.reduce(
      (sum, row) => sum + row.ntbPurchases,
      0
    );
    const totalPurchases = ntbData.behaviorByGroup.reduce((sum, row) => sum + row.purchases, 0);
    const ntbPurchaseRate =
      totalPurchases > 0 ? Number(((totalNtbPurchases / totalPurchases) * 100).toFixed(2)) : 0;
    return { ...ntbData.overview.revenue, ntbPurchaseRate };
  }, [ntbData]);

  const isBusy = isFetching || isLoading;

  return (
    <div className='amc-new-to-brand'>
      <div className='amc-new-to-brand-header'>
        <DatePicker
          range={dateRange}
          onChange={setDateRange}
          fullMonthsOnly
          fiscalCalOverride={{}}
          design={Design.NEUTRAL}
        />
      </div>

      {isBusy ? (
        <div className='amc-new-to-brand-cards-loading'>
          <Spinner />
        </div>
      ) : (
        ntbData?.overview.cost &&
        revenueCardData && (
          <AmcNewToBrandDataCardRow
            costCardData={ntbData.overview.cost}
            revenueCardData={revenueCardData}
          />
        )
      )}

      <div className='amc-new-to-brand-row'>
        <ChartContainer title='NTB CPA' isLoading={isBusy} minHeight={300}>
          <AmcLineChart
            data={ntbData?.overviewGraph ?? []}
            dataKey='ntbCpa'
            lineColor={GreenHeat100}
            tooltipKey='NTB CPA'
          />
        </ChartContainer>
        <ChartContainer title='NTB ROAS' isLoading={isBusy} minHeight={300}>
          <AmcLineChart
            data={ntbData?.overviewGraph ?? []}
            dataKey='ntbRoas'
            lineColor={Channel8}
            tooltipKey='NTB ROAS'
          />
        </ChartContainer>
      </div>

      <div className='amc-new-to-brand-row'>
        <ChartContainer title='Purchases by Customer Type' isLoading={isBusy} minHeight={400}>
          <AmcStackedBarChart
            data={ntbData?.ntbVsRepeatBehavior.purchases ?? []}
            dataKeys={['ntbCustomers', 'repeatCustomers']}
            percentKeys={['ntbCustomersPercent', 'repeatCustomersPercent']}
            fill={{ ntbCustomers: Channel4, repeatCustomers: Channel8 }}
            dataLabel={{
              ntbCustomersPercent: 'NTB Purchases',
              repeatCustomersPercent: 'Repeat Purchases'
            }}
          />
        </ChartContainer>
        <ChartContainer title='Customers by Type' isLoading={isBusy} minHeight={400}>
          <AmcStackedBarChart
            data={ntbData?.ntbVsRepeatBehavior.customers ?? []}
            dataKeys={['ntbCustomers', 'ntbRepeatCustomers']}
            percentKeys={['ntbCustomersPercent', 'ntbRepeatCustomersPercent']}
            fill={{ ntbCustomers: Channel6, ntbRepeatCustomers: Channel7 }}
            dataLabel={{
              ntbCustomersPercent: 'NTB Customers',
              ntbRepeatCustomersPercent: 'Repeat Customers'
            }}
          />
        </ChartContainer>
      </div>

      <ChartContainer
        title='Behavior by Group'
        isLoading={isBusy}
        minHeight={500}
        rightActions={
          <IconButton
            design={Design.NEUTRAL}
            onClick={() =>
              download(behaviorByGroupCsv, 'new-to-brand-behavior-by-group.csv', 'text/csv')
            }
          >
            <MdOutlineFileDownload />
          </IconButton>
        }
      >
        <div className='amc-new-to-brand-table-wrapper'>
          <BPMTable
            data={ntbData?.behaviorByGroup ?? []}
            filterBar={false}
            headers={getNtbTableHeaders(maxValues)}
            noRowsRenderer={NoNewToBrandData}
            pagination
            onCsvChange={setBehaviorByGroupCsv}
          />
        </div>
      </ChartContainer>
    </div>
  );
};
